import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { IApiQueryOptions } from 'api/ApiClient';
import { Button, FieldWrapper, Filter, FilterButton, Input, LayoutSidebar, PaginatedList, PayeesSideBar, SectionHeader, Select } from 'components';
import { DDOption, DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { getQueryStringValue, useDebounce, useQueryString } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employees, getEmployeesRequest, getJobTitlesRequest, selectEmployees, selectJobTitlesBySite } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getPayeesRequest, Payment } from 'store/Payment';
import { IJobTitleState, IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeRole, EmployeeType, IApiQueryParam, IEmployee, IPayee, ISiteRolePolicy } from 'types';
import { DEFAULT_SORT, formatJobTitle, isEmployeePayable } from 'utils';
import { MakePaymentHeader } from '../../components';
import { SelectPayeesTable } from '../../components/SelectPayeesTable';
import { MPContent } from '../../styles';
import { MakePaymentProps } from '../../types';

interface IPayeesListFiltersProps {
  filterActive: boolean;
  jobTitles: IJobTitleState[];
  onQueryChange: (options: IApiQueryOptions) => void;
  urlQuery: IApiQueryOptions;
}

const PayeesListFilters: React.FC<IPayeesListFiltersProps> = ({ filterActive, urlQuery, jobTitles, onQueryChange }) => {
  const payeeNameTextSearch = getQueryStringValue(urlQuery.q, 'name', ApiQueryOperator.like);
  const jobTitleSearch = getQueryStringValue(urlQuery.q, 'sites.job.title', ApiQueryOperator.like)?.slice(1, -1);
  const adminTypeFilter = getQueryStringValue(urlQuery.q, 'type', ApiQueryOperator.eq);
  const selectedJobTitleValue = adminTypeFilter === EmployeeType.ADMIN ? { title: EmployeeRole.ADMIN } : jobTitleSearch;
  const jobTitleOptions = [
    { label: 'All', value: null },
    { label: formatJobTitle(DEFAULT_ORG_ADMIN_JOB_TITLE), value: { title: EmployeeRole.ADMIN } },
    ...(jobTitles?.map((jobTitle) => ({ label: formatJobTitle(jobTitle.title), value: jobTitle.title })) || []),
  ];

  const onNameChange = (value: string) => {
    const otherFilters = (urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== 'name');
    const newValue = value.trim();
    const filters = newValue.length ? [...otherFilters, { key: 'name', operator: ApiQueryOperator.like, value: newValue }] : otherFilters;
    onQueryChange({ ...urlQuery, q: filters, offset: 0 });
  };
  const debouncedOnNameChange = useDebounce(onNameChange, 500);

  const onJobTitleChange = (selected: DDOption[]) => {
    const selectedValue = selected?.[0]?.value ?? null;
    const otherFilters = (urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== 'sites.job.title' && query.key !== 'type');

    if (!selectedValue) {
      onQueryChange({ ...urlQuery, q: otherFilters, offset: 0 });
      return;
    }

    const isAdmin = typeof selectedValue === 'object' && (selectedValue as { title?: string })?.title === EmployeeRole.ADMIN;
    const filters = [...otherFilters, isAdmin ? { key: 'type', operator: ApiQueryOperator.eq, value: EmployeeType.ADMIN } : { key: 'sites.job.title', operator: ApiQueryOperator.like, value: `^${selectedValue}$` }];
    onQueryChange({ ...urlQuery, q: filters, offset: 0 });
  };

  return (
    <Filter isVisible={filterActive} data-cy="payees-filters" nested={false}>
      <FieldWrapper label="Name" data-cy="name-filter">
        <Input placeholder="Name" defaultValue={payeeNameTextSearch} onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnNameChange(e.currentTarget.value)} />
      </FieldWrapper>
      <FieldWrapper label="Job Title" data-cy="job-title-filter">
        <Select placeholder="All" multi={false} options={jobTitleOptions} values={jobTitleOptions.filter((option) => isEqual(option.value, selectedJobTitleValue))} onChange={onJobTitleChange} dataCy="job-title-select" />
      </FieldWrapper>
    </Filter>
  );
};

interface ISelectPayeesProps extends MakePaymentProps {
  employees: IEmployee[];
  payees: IPayee[];
  rolePolicy: ISiteRolePolicy;
}

const SelectPayees = ({ employees, onRemovePayee, onBack, onNext, payees, pageHeaderContent, onAddPayees, rolePolicy, currentEmployee }: ISelectPayeesProps) => {
  const dispatch = useDispatch();
  const [urlQuery, setUrlQuery] = useQueryString('selectPayees', window.location, { sort: DEFAULT_SORT, limit: 25 });
  const [filterActive, setFilterActive] = useState<boolean>(true);

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const isLoadingAddAll = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.GET_PAYEES_REQUEST));
  const { total } = useSelector(selectEmployees()) || { data: [], total: null };
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentSite?.id;
  const jobTitles = useSelector(selectJobTitlesBySite(currentSiteId));

  useEffect(() => {
    if (filterActive && !jobTitles?.length) {
      dispatch(getJobTitlesRequest({ siteId: currentSiteId }));
    }
  }, [dispatch, currentSiteId, filterActive]);

  // Fetch employees when urlQuery changes
  useEffect(() => {
    const nonStatusQuery = (urlQuery.q || []).filter((q: IApiQueryParam) => q.key !== 'status');
    dispatch(getEmployeesRequest(AffiliationType.SITES, currentSiteId, { ...urlQuery, q: nonStatusQuery }));
  }, [currentSiteId, urlQuery]);

  const onQueryChange = (options: IApiQueryOptions) => setUrlQuery(options);

  const onAddToPayeesList = (id: string) => {
    const selectedEmployee = employees.find((e) => e.id === id);
    if (selectedEmployee) {
      onAddPayees([selectedEmployee]);
    }
  };

  const onAddAllEmployeesToPayeesList = (query: IApiQueryOptions) => {
    const { sort, meta, q } = query;
    const currentSiteId = meta as string;
    const nonStatusQuery = (q || []).filter((q: IApiQueryParam) => q.key !== 'status');

    const onSuccess = (allEmployees: IEmployee[]) => {
      // filter employees that are not eligible for tips
      onAddPayees(allEmployees.filter((emp) => isEmployeePayable(emp, rolePolicy, currentEmployee)));
    };
    dispatch(getPayeesRequest(currentSiteId, { sort, limit: 0, q: nonStatusQuery }, onSuccess));
  };

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader pageHeaderContent={pageHeaderContent} />
          <PaginatedList total={total} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={onQueryChange} isLoading={isLoading} standalone>
            <MPContent>
              <SectionHeader title="Select Payees">
                <FilterButton data-cy="toggle-payees-filters-btn" filterActive={filterActive} appliedFilters={urlQuery.q?.filter((item: IApiQueryParam) => item.key !== 'status').length} onClick={() => setFilterActive(!filterActive)} />
                <Button
                  data-cy="add-all-btn"
                  label="Add All"
                  appearance="primary"
                  size="small"
                  onClick={() => onAddAllEmployeesToPayeesList({ ...urlQuery, meta: currentSiteId })}
                  isProcessing={isLoadingAddAll}
                  disabled={isLoading || isLoadingAddAll || employees?.length === 0}
                />
              </SectionHeader>

              {employees && <PayeesListFilters filterActive={filterActive} urlQuery={urlQuery} jobTitles={jobTitles} onQueryChange={onQueryChange} />}

              <SelectPayeesTable
                onAddToPayeesList={onAddToPayeesList}
                onSortChange={(newSort: string) => setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort })}
                rolePolicy={rolePolicy}
                currentEmployee={currentEmployee}
                employees={employees}
                payees={payees}
                isLoading={isLoading || isLoadingAddAll}
                urlQuery={urlQuery}
              />
            </MPContent>
          </PaginatedList>
        </>
      }
      sidebar={<PayeesSideBar payees={payees} onBack={onBack} onContinue={onNext} onRemovePayee={onRemovePayee} isLoading={isLoading || isLoadingAddAll}></PayeesSideBar>}
    />
  );
};

export default SelectPayees;
